// Asset allocation for the "Analytics" tab (docs/analytics/SPEC.md): position values in RUB
// grouped by type, sector → industry, security and currency; every share lists its securities.
import { intlLocale, locale, t } from './i18n';
import { instrumentTypeLabel, toNumber } from './format';

const TOTAL_CURRENCY = 'RUB';
// Securities shown on their own in the companies block; the rest is one gray row and arc (decision 16).
const RING_COMPANIES = 10;

// Distinct colors on the dark theme; slices take them in order of size.
export const PALETTE = [
  '#34d399', '#60a5fa', '#fbbf24', '#f472b6', '#a78bfa', '#f87171', '#2dd4bf', '#fb923c',
  '#a3e635', '#38bdf8', '#e879f9', '#facc15', '#94a3b8', '#4ade80', '#c084fc', '#fda4af',
];
export const OTHERS_COLOR = '#64748b';

const TYPE_ORDER = ['share', 'bond', 'etf', 'currency', 'other'];

// Live price when the daemon has one, otherwise the snapshot (decision 3, as in PortfolioCard);
// bonds add the accrued coupon, as the broker's total does.
export function positionValueRub(position, livePrices = {}, fxRates = {}) {
  const base = toNumber(livePrices[position.instrument_uid]?.price) ?? toNumber(position.current_price);
  const price = base === null ? null : base + (toNumber(position.current_nkd) ?? 0);
  const qty = toNumber(position.quantity);
  const rate = position.currency === TOTAL_CURRENCY ? 1 : toNumber(fxRates[position.currency]);
  if (price === null || qty === null || rate === null || position.outside_total) {
    return null;
  }
  return price * qty * rate;
}

function typeOf(position) {
  const type = position.instrument_type;
  return TYPE_ORDER.includes(type) ? type : 'other';
}

function industryName(industry) {
  return locale.value === 'en' ? industry.name_en : industry.name_ru;
}

// Sector of a position: the industry's sector; cash and unlabelled securities get their own (decision 5).
function sectorOf(position) {
  if (typeOf(position) === 'currency') {
    return 'cash';
  }
  return position.industry?.sector ?? 'unlabelled';
}

// "RUB" → "Российский рубль (RUB)"; the code alone when the browser does not know it.
function currencyName(code) {
  try {
    const name = new Intl.DisplayNames([intlLocale()], { type: 'currency' }).of(code);
    return name && name !== code ? `${name.charAt(0).toUpperCase()}${name.slice(1)} (${code})` : code;
  } catch {
    return code;
  }
}

function sectorLabel(sector) {
  return t(`sector.${sector}`);
}

function itemKey(position) {
  return position.instrument_uid || String(position.id);
}

function add(groups, key, label, entry, extra = {}) {
  if (!groups.has(key)) {
    groups.set(key, { label, value: 0, entries: [], ...extra });
  }
  const group = groups.get(key);
  group.value += entry.value;
  group.entries.push(entry);
  return group;
}

/**
 * Groups → slices sorted by size: share of `base`, a color, and the securities behind the share
 * (`items`, each with its share of the whole portfolio, decision 10).
 */
function toSlices(groups, base, portfolioTotal, { order = null } = {}) {
  const slices = [...groups.entries()].map(([key, g]) => ({
    key,
    label: g.label,
    value: g.value,
    percent: base ? (g.value / base) * 100 : 0,
    groups: g.children ?? null,
    items: toItems(g.entries, portfolioTotal),
  }));
  slices.sort(order ?? ((a, b) => b.value - a.value));
  slices.forEach((s, i) => {
    s.color = PALETTE[i % PALETTE.length];
  });
  return slices;
}

function toItems(entries, portfolioTotal) {
  return [...entries]
    .sort((a, b) => b.value - a.value)
    .map(({ position, value }) => ({
      key: itemKey(position),
      position,
      value,
      percent: portfolioTotal ? (value / portfolioTotal) * 100 : 0,
    }));
}

export function buildAllocation(positions, livePrices = {}, fxRates = {}) {
  const valued = [];
  let skipped = 0;
  for (const position of positions ?? []) {
    const value = positionValueRub(position, livePrices, fxRates);
    if (value === null) {
      skipped += 1;
      continue;
    }
    if (value > 0) {
      valued.push({ position, value });
    }
  }
  const total = valued.reduce((sum, v) => sum + v.value, 0);

  const types = new Map();
  const sectors = new Map();
  const currencies = new Map();
  for (const entry of valued) {
    const { position } = entry;
    const type = typeOf(position);
    add(types, type, type === 'currency' ? t('analytics.cash') : instrumentTypeLabel(type), entry);

    const sector = sectorOf(position);
    const group = add(sectors, sector, sectorLabel(sector), entry, { children: new Map() });
    const industry = position.industry;
    add(group.children, industry ? industry.code : sector, industry ? industryName(industry) : sectorLabel(sector), entry);

    const currency = (position.currency || '').toUpperCase() || '?';
    add(currencies, currency, currencyName(currency), entry);
  }

  const sectorSlices = toSlices(sectors, total, total);
  for (const slice of sectorSlices) {
    // Industries inside a sector: share of the sector on its ring, items as everywhere.
    slice.groups = toSlices(slice.groups, slice.value, total);
  }

  return {
    total,
    skipped,
    types: toSlices(types, total, total, { order: (a, b) => TYPE_ORDER.indexOf(a.key) - TYPE_ORDER.indexOf(b.key) }),
    sectors: sectorSlices,
    companies: companies(valued, total),
    currencies: toSlices(currencies, total, total),
  };
}

// Every security without cash (decisions 7a, 16): each in its own palette color; `others` sums up all
// but the top 10 — one gray row and arc until "Show more". Shares are of the whole portfolio, as on
// the "Portfolio" tab.
function companies(valued, portfolioTotal) {
  const securities = valued.filter((v) => typeOf(v.position) !== 'currency');
  const total = securities.reduce((sum, v) => sum + v.value, 0);
  const groups = new Map();
  for (const entry of securities) {
    const p = entry.position;
    add(groups, itemKey(p), p.name || p.ticker || p.instrument_uid || '?', entry);
  }
  const slices = toSlices(groups, portfolioTotal, portfolioTotal);
  slices.forEach((slice) => {
    slice.position = slice.items[0].position;
  });

  const rest = slices.slice(RING_COMPANIES);
  let others = null;
  if (rest.length > 1) {
    const value = rest.reduce((sum, s) => sum + s.value, 0);
    others = {
      key: 'others',
      label: t('analytics.othersRing', { count: rest.length }),
      value,
      percent: portfolioTotal ? (value / portfolioTotal) * 100 : 0,
      color: OTHERS_COLOR,
      members: rest.map((s) => s.key),
    };
  }

  const topShare = (n) => slices.slice(0, n).reduce((sum, s) => sum + s.percent, 0);
  return {
    total,
    slices,
    others,
    count: slices.length,
    concentration: slices.length > 5 ? { top5: topShare(5), top10: slices.length > 10 ? topShare(10) : null } : null,
  };
}
